/**
 * src/pages/AboutPage.js — Growing business focus, no years of experience
 */
import React from 'react';
import { Link } from 'react-router-dom';

const PILLARS = [
  {
    icon: '🚀',
    title: 'A Growing Business',
    text: 'We are a passionate, growing team constantly improving our service, expanding our product range, and finding better ways to reach and delight our customers. Every order we fulfill pushes us forward.',
    color: 'from-red-600 to-red-800',
  },
  {
    icon: '⚖️',
    title: 'Supreme Court Compliant',
    text: 'We strictly follow all Supreme Court of India regulations on firecracker sales. Compliance is not optional for us — it\'s the foundation of how we operate. Every product, every process, by the book.',
    color: 'from-yellow-500 to-amber-600',
  },
  {
    icon: '🛡️',
    title: 'Safety & Quality',
    text: 'All our products are sourced only from licensed, certified manufacturers. We believe safe celebrations are joyful celebrations — every item is checked before it reaches your hands.',
    color: 'from-orange-500 to-red-600',
  },
  {
    icon: '🤝',
    title: 'Customer Trust Above All',
    text: 'Your trust is everything to us. We respond to every enquiry personally within 24 hours. Transparent pricing, honest communication, and zero compromise on service — that\'s our promise.',
    color: 'from-emerald-500 to-teal-600',
  },
];

const STATS = [
  { value: '150+', label: 'Products Available' },
  { value: '100+', label: 'Happy Customers' },
  { value: '100%', label: 'Licensed Products' },
  { value: '24hr', label: 'Response Guarantee' },
];

const VALUES = [
  { icon: '💡', title: 'Continuous Improvement', desc: 'We are always learning, adapting, and improving to serve you better.' },
  { icon: '❤️', title: 'Passion for Celebrations', desc: 'We genuinely love what we do — helping families celebrate safely and joyfully.' },
  { icon: '🌿', title: 'Responsible Practices', desc: 'We promote eco-friendly choices and responsible celebration at every step.' },
  { icon: '📞', title: 'Personal Service', desc: 'Real people, real conversations — we connect with every customer directly.' },
];

export default function AboutPage() {
  // ── SEO: update page title and meta description ───────────────
  React.useEffect(() => {
    document.title = 'About Us — Lakshmi Crackers | Sivakasi Fireworks Shop';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Lakshmi Crackers is a trusted fireworks shop in Sivakasi, Tamil Nadu. We source directly from licensed manufacturers and deliver quality crackers at affordable prices across India.');
    return () => {
      document.title = 'Lakshmi Crackers 🪔 — Premium Fireworks';
    };
  }, []);

  return (
    <div className="min-h-screen" style={{ background: '#fffaf0' }}>

      {/* Hero */}
      <div className="relative bg-gradient-to-br from-red-950 via-red-800 to-red-700 overflow-hidden">
        <div className="absolute inset-0 deco-stripes opacity-30 pointer-events-none" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-yellow-400/10 rounded-full blur-[80px] pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <span className="inline-flex items-center gap-2 bg-yellow-400/15 border border-yellow-400/25
            text-yellow-300 text-xs font-bold px-5 py-2 rounded-full uppercase tracking-widest mb-6">
            <span className="w-1.5 h-1.5 bg-yellow-400 rounded-full animate-pulse" />
            Sivakasi, Tamil Nadu
          </span>
          <h1 className="text-5xl sm:text-6xl font-black text-white mb-4"
            style={{ fontFamily: '"Playfair Display",serif' }}>
            About{' '}
            <span style={{ color: '#f5c842' }}>
              Lakshmi Crackers
            </span>
          </h1>
          <p className="text-red-100/70 text-lg max-w-2xl mx-auto leading-relaxed">
            We are a growing, passionate business committed to bringing safe,
            high-quality fireworks to your celebrations — backed by trust,
            compliance, and a genuine love for what we do.
          </p>
        </div>
        <svg className="absolute bottom-0 left-0 right-0 w-full h-8" viewBox="0 0 1440 32" fill="none" preserveAspectRatio="none">
          <path d="M0,20 C480,40 960,0 1440,20 L1440,32 L0,32 Z" fill="#fffaf0" />
        </svg>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-20">

        {/* Mission statement */}
        <div className="text-center">
          <div className="inline-block bg-white rounded-3xl shadow-[0_4px_32px_rgba(192,21,42,0.08)]
            border border-yellow-200/40 px-8 py-10 max-w-3xl">
            <div className="text-4xl mb-4">🔥</div>
            <p className="text-3xl font-black text-gray-900 leading-snug mb-4"
              style={{ fontFamily: '"Playfair Display",serif' }}>
              "We are growing and continuously improving — because you deserve better every time."
            </p>
            <p className="text-gray-500 text-sm leading-relaxed">
              At Lakshmi Crackers, we aim to provide safe, joyful celebrations for every family.
              We stock only government-certified, quality-checked products from trusted Sivakasi manufacturers,
              and we handle every enquiry with a personal touch — because we genuinely care about your experience.
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {STATS.map((s) => (
            <div key={s.label}
              className="bg-white rounded-2xl border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.05)]
                p-6 text-center hover:-translate-y-1 hover:shadow-[0_12px_32px_rgba(192,21,42,0.1)]
                transition-all duration-300">
              <p className="text-3xl font-black mb-1"
                style={{
                  fontFamily: '"Plus Jakarta Sans",sans-serif',
                  color: '#c0152a',
                }}>
                {s.value}
              </p>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Core pillars */}
        <div>
          <div className="text-center mb-10">
            <p className="text-red-500 text-xs font-black uppercase tracking-widest mb-2">What Drives Us</p>
            <h2 className="text-3xl font-black text-gray-900" style={{ fontFamily: '"Playfair Display",serif' }}>
              Our Core Commitments
            </h2>
            <div className="w-16 h-0.5 bg-gradient-to-r from-transparent via-yellow-400 to-transparent mx-auto mt-3" />
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {PILLARS.map((p) => (
              <div key={p.title}
                className="bg-white rounded-2xl border border-gray-100 p-7
                  shadow-[0_2px_12px_rgba(0,0,0,0.05)]
                  hover:shadow-[0_12px_32px_rgba(192,21,42,0.1)] hover:-translate-y-1
                  transition-all duration-300 group">
                <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${p.color}
                  flex items-center justify-center text-2xl mb-5 shadow-md
                  group-hover:scale-110 transition-transform duration-300`}>
                  {p.icon}
                </div>
                <h3 className="font-black text-gray-900 text-lg mb-2"
                  style={{ fontFamily: '"Plus Jakarta Sans",sans-serif' }}>
                  {p.title}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Values */}
        <div>
          <div className="text-center mb-8">
            <p className="text-red-500 text-xs font-black uppercase tracking-widest mb-2">How We Work</p>
            <h2 className="text-3xl font-black text-gray-900" style={{ fontFamily: '"Playfair Display",serif' }}>
              Our Values in Action
            </h2>
            <div className="w-16 h-0.5 bg-gradient-to-r from-transparent via-yellow-400 to-transparent mx-auto mt-3" />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {VALUES.map((v) => (
              <div key={v.title}
                className="bg-white rounded-2xl border border-gray-100 p-5 text-center
                  shadow-[0_2px_8px_rgba(0,0,0,0.04)]
                  hover:shadow-[0_8px_24px_rgba(192,21,42,0.08)] hover:-translate-y-0.5
                  transition-all duration-200">
                <div className="text-3xl mb-3">{v.icon}</div>
                <h4 className="font-black text-gray-900 text-sm mb-1.5"
                  style={{ fontFamily: '"Plus Jakarta Sans",sans-serif' }}>
                  {v.title}
                </h4>
                <p className="text-gray-500 text-xs leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Legal compliance */}
        <div className="bg-gradient-to-br from-red-950 to-red-900 rounded-3xl p-8 text-center relative overflow-hidden">
          <div className="absolute inset-0 deco-stripes opacity-20 pointer-events-none" />
          <div className="relative">
            <span className="text-4xl block mb-4">⚖️</span>
            <h3 className="text-xl font-black text-yellow-300 mb-3"
              style={{ fontFamily: '"Playfair Display",serif' }}>
              Legal Compliance — Non-Negotiable
            </h3>
            <p className="text-red-100/70 text-sm leading-relaxed max-w-2xl mx-auto mb-3">
              In accordance with the Supreme Court of India's directives,{' '}
              <span className="text-yellow-300 font-bold">online sale of firecrackers is restricted</span>.
              Lakshmi Crackers operates as an enquiry platform — once you submit your order, our team
              contacts you within 24 hours to guide you through the safe, legal purchase process.
            </p>
            <p className="text-red-300/50 text-xs">
              PESO licensed · Tamil Nadu state regulations compliant · Supreme Court guidelines followed
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center py-2">
          <p className="text-gray-500 text-sm mb-6">
            Ready to celebrate with Lakshmi Crackers?
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/products"
              className="bg-gradient-to-br from-red-600 to-red-800 text-white font-black
                px-8 py-4 rounded-2xl shadow-xl shadow-red-600/30
                hover:shadow-red-600/50 hover:-translate-y-0.5 transition-all duration-200">
              🎆 Browse Products
            </Link>
            <Link to="/safety"
              className="border-2 border-red-200 text-red-700 font-bold
                px-8 py-4 rounded-2xl hover:bg-red-50 hover:-translate-y-0.5 transition-all duration-200">
              🛡️ Safety Guidelines
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
